import React, { useState, useEffect } from 'react'
import Layout from '../components/Layout'
import api from '../services/api'
import { useModal } from '../components/Modal'

export default function UsersPage() {
  const { showAlert, showConfirm, ModalComponents } = useModal()
  const [users, setUsers] = useState([])
  const [divisions, setDivisions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editData, setEditData] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showResetModal, setShowResetModal] = useState(false)
  const [resetUser, setResetUser] = useState(null)
  const [newPassword, setNewPassword] = useState('')
  const [resetError, setResetError] = useState('')
  const [form, setForm] = useState({
    username: '', password: '', full_name: '', role: 'hr',
    division_ids: [], is_active: true, is_witness: false, signature_image: ''
  })

  useEffect(() => { fetchUsers(); fetchDivisions() }, [])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const res = await api.get('/users')
      if (res.data.success) setUsers(res.data.data)
    } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const fetchDivisions = async () => {
    try {
      const res = await api.get('/divisions')
      if (res.data.success) setDivisions(res.data.data)
    } catch (err) { console.error(err) }
  }

  const openAddModal = () => {
    setEditData(null)
    setForm({ username: '', password: '', full_name: '', role: 'hr', division_ids: [], is_active: true, is_witness: false, signature_image: '' })
    setError('')
    setShowModal(true)
  }

  const openEditModal = (user) => {
    setEditData(user)
    setForm({ username: user.username, password: '', full_name: user.full_name, role: user.role, division_ids: user.division_ids || [], is_active: user.is_active, is_witness: user.is_witness === true, signature_image: user.signature_image || '' })
    setError('')
    setShowModal(true)
  }

  const handleSave = async () => {
    setError('')
    if (!form.username || !form.full_name) { setError('กรุณากรอก Username และชื่อ-นามสกุล'); return }
    if (!editData && !form.password) { setError('กรุณากรอกรหัสผ่าน'); return }
    if (form.role === 'clerk' && form.division_ids.length === 0) { setError('กรุณาเลือกกองงานที่ดูแล'); return }
    try {
      setSaving(true)
      if (editData) await api.put(`/users/${editData.id}`, form)
      else await api.post('/users', form)
      setShowModal(false)
      fetchUsers()
    } catch (err) { setError(err.response?.data?.message || 'เกิดข้อผิดพลาด') }
    finally { setSaving(false) }
  }

  const handleUnlock = async (user) => {
    const ok = await showConfirm('warning', 'ปลดล็อค Account', `ยืนยันการปลดล็อค ${user.full_name} ?`)
    if (!ok) return
    try {
      await api.put(`/users/${user.id}`, { username: user.username, full_name: user.full_name, role: user.role, is_active: user.is_active, is_witness: user.is_witness === true, division_ids: user.division_ids || [], signature_image: user.signature_image || null, unlock: true })
      showAlert('success', 'ปลดล็อคสำเร็จ', `ปลดล็อค ${user.full_name} สำเร็จแล้ว`)
      fetchUsers()
    } catch (err) { showAlert('error', 'เกิดข้อผิดพลาด', err.response?.data?.message || 'เกิดข้อผิดพลาด') }
  }

  const handleResetPassword = async () => {
    setResetError('')
    if (!newPassword) { setResetError('กรุณากรอกรหัสผ่านใหม่'); return }
    if (newPassword.length < 12 || new TextEncoder().encode(newPassword).length > 72) { setResetError('รหัสผ่านต้องมีอย่างน้อย 12 ตัวอักษร และไม่เกิน 72 ไบต์'); return }
    const ok = await showConfirm('warning', 'ยืนยัน Reset', `Reset รหัสผ่านของ ${resetUser.full_name} ใช่หรือไม่?`)
    if (!ok) return
    try {
      await api.put(`/users/${resetUser.id}`, { username: resetUser.username, full_name: resetUser.full_name, role: resetUser.role, is_active: resetUser.is_active, is_witness: resetUser.is_witness === true, division_ids: resetUser.division_ids || [], signature_image: resetUser.signature_image || null, password: newPassword })
      setShowResetModal(false)
      showAlert('success', 'Reset สำเร็จ', `${resetUser.full_name} ต้องตั้งรหัสผ่านของตนเองเมื่อเข้าสู่ระบบครั้งถัดไป`)
    } catch (err) { showAlert('error', 'เกิดข้อผิดพลาด', err.response?.data?.message || 'เกิดข้อผิดพลาด') }
  }

  const handleToggleActive = async (user) => {
    const ok = await showConfirm(user.is_active ? 'danger' : 'success', user.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน', `${user.is_active ? 'ปิด' : 'เปิด'}ใช้งาน ${user.full_name} ใช่หรือไม่?`)
    if (!ok) return
    try {
      await api.patch(`/users/${user.id}/status`, { is_active: !user.is_active })
      fetchUsers()
    } catch (err) { showAlert('error', 'เกิดข้อผิดพลาด', 'ไม่สามารถเปลี่ยนสถานะได้') }
  }

  const handleDivisionToggle = (divId) => {
    const ids = form.division_ids.includes(divId) ? form.division_ids.filter(id => id !== divId) : [...form.division_ids, divId]
    setForm({ ...form, division_ids: ids })
  }

  const roleConfig = {
    hr:    { label: 'HR',           color: '#3b82f6', bg: '#eff6ff' },
    mayor: { label: 'นายกเทศมนตรี', color: '#16a34a', bg: '#f0fdf4' },
    clerk: { label: 'ธุรการ',        color: '#8b5cf6', bg: '#f5f3ff' },
    admin: { label: 'Admin',         color: '#ef4444', bg: '#fef2f2' },
  }

  const inputStyle = { width: '100%', padding: '9px 12px', border: '0.5px solid #e2e8f0', borderRadius: '9px', fontSize: '13px', color: '#0f172a', background: 'white', outline: 'none', fontFamily: 'inherit' }

  return (
    <Layout>
      <ModalComponents />
      <div className="ice-list-page" style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: '500', color: '#0f172a' }}>จัดการผู้ใช้ระบบ</h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px' }}>HR, ธุรการ, นายกเทศมนตรี</p>
          </div>
          <button onClick={openAddModal}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', background: '#075dcc', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
            <i className="ti ti-user-plus" style={{ fontSize: '16px' }} />
            เพิ่มผู้ใช้
          </button>
        </div>

        {/* Table */}
        <div style={{ background: 'white', borderRadius: '14px', border: '0.5px solid #e2e8f0', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', fontSize: '13px' }}>กำลังโหลด...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['ชื่อ-นามสกุล', 'Username', 'สิทธิ์', 'พยาน', 'กองงาน', 'ลายเซ็น', 'สถานะ', 'จัดการ'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 16px', fontSize: '11px', color: '#94a3b8', fontWeight: '500' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => {
                    const rc = roleConfig[user.role] || { label: user.role, color: '#64748b', bg: '#f1f5f9' }
                    return (
                      <tr key={user.id} style={{ borderTop: '0.5px solid #f1f5f9' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>{user.full_name}</td>
                        <td style={{ padding: '12px 16px', fontSize: '12px', color: '#94a3b8' }}>{user.username}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '500', color: rc.color, background: rc.bg }}>{rc.label}</span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {user.role === 'hr' && user.is_witness ? (
                            <span style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '600', color: '#b45309', background: '#fef3c7' }}>พยาน</span>
                          ) : (
                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '12px', color: '#64748b' }}>{user.division_names?.join(', ') || '-'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          {user.signature_image ? (
                            <img src={user.signature_image} alt="ลายเซ็น" style={{ maxHeight: '32px', border: '0.5px solid #e2e8f0', borderRadius: '4px', background: 'white', padding: '2px' }} />
                          ) : (
                            <span style={{ fontSize: '11px', color: '#ef4444' }}>ยังไม่มี</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '500', color: user.is_active ? '#22c55e' : '#94a3b8', background: user.is_active ? '#f0fdf4' : '#f8fafc' }}>
                            {user.is_active ? 'ใช้งาน' : 'ปิดใช้งาน'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <button onClick={() => openEditModal(user)}
                              style={{ padding: '4px 10px', background: '#eff6ff', color: '#075dcc', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>แก้ไข</button>
                            <button onClick={() => { setResetUser(user); setNewPassword(''); setResetError(''); setShowResetModal(true) }}
                              style={{ padding: '4px 10px', background: '#fff7ed', color: '#f97316', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>Reset รหัส</button>
                            {user.locked_until && new Date(user.locked_until) > new Date() && (
                              <button onClick={() => handleUnlock(user)}
                                style={{ padding: '4px 10px', background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>🔒 ปลดล็อค</button>
                            )}
                            <button onClick={() => handleToggleActive(user)}
                              style={{ padding: '4px 10px', background: user.is_active ? '#fef2f2' : '#f0fdf4', color: user.is_active ? '#ef4444' : '#22c55e', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                              {user.is_active ? 'ปิด' : 'เปิด'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Reset Password Modal */}
      {showResetModal && resetUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '360px', overflow: 'hidden' }}>
            <div style={{ background: '#fff7ed', padding: '24px', textAlign: 'center' }}>
              <i className="ti ti-key" style={{ fontSize: '40px', color: '#f97316', display: 'block', marginBottom: '10px' }} />
              <p style={{ fontSize: '16px', fontWeight: '500', color: '#9a3412' }}>Reset รหัสผ่าน</p>
              <p style={{ fontSize: '12px', color: '#f97316', marginTop: '4px' }}>{resetUser.full_name}</p>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '6px' }}>รหัสผ่านใหม่</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="รหัสชั่วคราว (อย่างน้อย 12 ตัวอักษร)"
                style={{ ...inputStyle, marginBottom: '4px' }} />
              {resetError && <p style={{ fontSize: '11px', color: '#ef4444', marginBottom: '10px' }}>{resetError}</p>}
              <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                <button onClick={() => setShowResetModal(false)}
                  style={{ flex: 1, padding: '10px', background: 'white', color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', cursor: 'pointer' }}>ยกเลิก</button>
                <button onClick={handleResetPassword}
                  style={{ flex: 1, padding: '10px', background: '#f97316', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>ยืนยัน Reset</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '0.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ fontSize: '15px', fontWeight: '500', color: '#0f172a' }}>{editData ? 'แก้ไขผู้ใช้' : 'เพิ่มผู้ใช้ใหม่'}</p>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '20px' }}>×</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              {error && (
                <div style={{ background: '#fef2f2', border: '0.5px solid #fecaca', color: '#ef4444', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px' }}>{error}</div>
              )}
              <p style={{ fontSize: '12px', color: '#526e87', marginBottom: '12px' }}>รหัสที่แอดมินกำหนดเป็นรหัสชั่วคราว ผู้ใช้ต้องตั้งรหัสของตนเองเมื่อเข้าสู่ระบบครั้งแรก</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[
                  { label: 'ชื่อ-นามสกุล', key: 'full_name', type: 'text' },
                  { label: 'Username', key: 'username', type: 'text' },
                  { label: `รหัสผ่าน${editData ? ' (เว้นว่างถ้าไม่เปลี่ยน)' : ''}`, key: 'password', type: 'password', placeholder: editData ? 'เว้นว่างถ้าไม่เปลี่ยน' : 'รหัสผ่าน' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '5px' }}>{f.label}</label>
                    <input type={f.type} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      disabled={f.disabled} placeholder={f.placeholder}
                      style={{ ...inputStyle, background: f.disabled ? '#f8fafc' : 'white' }} />
                  </div>
                ))}

                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '5px' }}>สิทธิ์การใช้งาน</label>
                  <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value, division_ids: [], is_witness: e.target.value === 'hr' ? form.is_witness : false })} style={inputStyle}>
                    <option value="hr">HR</option>
                    <option value="clerk">ธุรการ</option>
                    <option value="mayor">นายกเทศมนตรี</option>
                  </select>
                </div>

                {form.role === 'clerk' && (
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '8px' }}>กองงานที่ดูแล</label>
                    <div style={{ border: '0.5px solid #e2e8f0', borderRadius: '10px', padding: '12px', maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {divisions.map(d => (
                        <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                          <input type="checkbox" checked={form.division_ids.includes(d.id)} onChange={() => handleDivisionToggle(d.id)} />
                          <span style={{ fontSize: '13px', color: '#334155' }}>{d.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {form.role === 'hr' && (
                  <div style={{ padding: '12px 14px', background: form.is_witness ? '#fff7ed' : '#f8fafc', border: `1px solid ${form.is_witness ? '#fed7aa' : '#e2e8f0'}`, borderRadius: '10px' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.is_witness} onChange={e => setForm({ ...form, is_witness: e.target.checked })} style={{ marginTop: '3px' }} />
                      <span>
                        <span style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#92400e' }}>กำหนดเป็นพยานลงนามสัญญา</span>
                        <span style={{ display: 'block', fontSize: '11px', color: '#64748b', marginTop: '3px' }}>เฉพาะบัญชีที่เปิดสิทธิ์นี้เท่านั้นจึงจะเห็นปุ่มตรวจสอบและลงนามในหน้ารออนุมัติ</span>
                      </span>
                    </label>
                  </div>
                )}

                {(form.role === 'hr' || form.role === 'mayor') && (
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', color: '#374151', marginBottom: '8px' }}>รูปลายเซ็น (PNG/JPG)</label>
                    {form.signature_image && (
                      <div style={{ marginBottom: '10px', padding: '10px', border: '0.5px solid #e2e8f0', borderRadius: '10px', background: '#f8fafc', textAlign: 'center' }}>
                        <img src={form.signature_image} alt="ลายเซ็น" style={{ maxHeight: '60px', margin: '0 auto' }} />
                        <button onClick={() => setForm({ ...form, signature_image: '' })}
                          style={{ fontSize: '11px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', marginTop: '6px', display: 'block', width: '100%' }}>
                          ลบรูปลายเซ็น
                        </button>
                      </div>
                    )}
                    <input type="file" accept="image/png,image/jpeg"
                      onChange={e => {
                        const file = e.target.files[0]
                        if (!file) return
                        if (file.size > 2 * 1024 * 1024) { showAlert('warning', 'ไฟล์ใหญ่เกินไป', 'รูปต้องไม่เกิน 2MB'); return }
                        const reader = new FileReader()
                        reader.onload = ev => setForm({ ...form, signature_image: ev.target.result })
                        reader.readAsDataURL(file)
                      }}
                      style={{ fontSize: '12px', color: '#64748b' }} />
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                  <span style={{ fontSize: '13px', color: '#334155' }}>เปิดใช้งาน</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button onClick={() => setShowModal(false)}
                  style={{ flex: 1, padding: '10px', background: 'white', color: '#64748b', border: '0.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', cursor: 'pointer' }}>ยกเลิก</button>
                <button onClick={handleSave} disabled={saving}
                  style={{ flex: 1, padding: '10px', background: saving ? '#a5b4fc' : '#075dcc', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
